import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import AuthSplit from "layouts/auth/types/AuthSplit";
import { Button } from "components/ui/Button";
import env from "variables/config";
import { AuthDivider, AuthHeading, EMAIL_RE, Field, FormAlert, GoogleButton, linkClass, PasswordField, storeSession } from "views/auth/authParts";

const GOOGLE_ERROR = "Inscription avec Google impossible. Réessayez, ou utilisez votre email.";

type FieldErrors = Partial<Record<"name" | "email" | "password" | "accessCode", string>>;

function SignUpDefault() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const urlCode = searchParams.get("trial_acces");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [accessCode, setAccessCode] = useState((urlCode ?? "").trim().toUpperCase());
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [formError, setFormError] = useState<string | null>(null);

    const clearError = (key: keyof FieldErrors) => {
        setErrors((prev) => ({ ...prev, [key]: undefined }));
        setFormError(null);
    };

    const loginGoogle = (token: string) => {
        setFormError(null);
        axios
            .post(`${env.apiUrl}/auth/google`, { token, mode: "register" })
            .then((response: any) => {
                if (!response.data.token) throw new Error("No token");
                storeSession(response.data);
                localStorage.setItem("accessCodeValidated", String(response.data.accessCodeValidated !== false));
                if (response.data.accessCodeValidated === false) {
                    navigate(urlCode ? `/auth/access-code?trial_acces=${encodeURIComponent(urlCode)}` : "/auth/access-code");
                } else {
                    navigate("/");
                }
            })
            .catch(() => setFormError(GOOGLE_ERROR));
    };

    const register = (e: FormEvent) => {
        e.preventDefault();
        const normalizedEmail = email.trim().toLowerCase();
        const normalizedCode = accessCode.trim().toUpperCase();
        const next: FieldErrors = {
            name: name.trim() ? undefined : "Saisissez votre nom et prénom.",
            email: !normalizedEmail
                ? "Saisissez votre email professionnel."
                : !EMAIL_RE.test(normalizedEmail)
                ? "Cet email ne semble pas valide."
                : undefined,
            password: password ? undefined : "Choisissez un mot de passe.",
            // Codes are checked by the server (managed in /bo/access-codes): a wrong one answers 400
            accessCode: normalizedCode ? undefined : "Saisissez votre code d'accès.",
        };
        setErrors(next);
        setFormError(null);
        if (Object.values(next).some(Boolean)) return;

        setLoading(true);
        axios
            .post(`${env.apiUrl}/auth/register`, {
                fullName: name.trim(),
                email: normalizedEmail,
                password,
                accessCode: normalizedCode,
            })
            .then((res: any) => {
                if (!res.data?.token) throw new Error("No token");
                storeSession(res.data);
                localStorage.setItem("accessCodeValidated", "true");
                navigate("/");
            })
            .catch((error: any) => {
                const status = error?.response?.status;
                if (status === 400) setErrors((prev) => ({ ...prev, accessCode: "Ce code d'accès n'est pas valide." }));
                else if (status === 401) setFormError("Un compte existe déjà avec cet email. Connectez-vous ou réinitialisez votre mot de passe.");
                else setFormError("Inscription impossible pour le moment. Réessayez dans quelques instants.");
            })
            .finally(() => setLoading(false));
    };

    return (
        <AuthSplit>
            <AuthHeading title="Créer votre compte" subtitle="Accédez à la vue complète du marché CHR." />

            <GoogleButton onToken={loginGoogle} onError={() => setFormError(GOOGLE_ERROR)} />

            <AuthDivider />

            {formError && <FormAlert>{formError}</FormAlert>}

            <form onSubmit={register} noValidate className="space-y-4">
                <Field
                    id="name"
                    label="Nom complet"
                    autoComplete="name"
                    autoFocus
                    placeholder="Prénom Nom"
                    value={name}
                    error={errors.name}
                    onChange={(e) => {
                        setName(e.target.value);
                        clearError("name");
                    }}
                />

                <Field
                    id="email"
                    label="Email professionnel"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="prenom@entreprise.fr"
                    value={email}
                    error={errors.email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        clearError("email");
                    }}
                />

                <PasswordField
                    id="password"
                    label="Mot de passe"
                    autoComplete="new-password"
                    placeholder="Choisissez un mot de passe"
                    value={password}
                    error={errors.password}
                    onChange={(e) => {
                        setPassword(e.target.value);
                        clearError("password");
                    }}
                />

                <Field
                    id="accessCode"
                    label="Code d'accès"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    placeholder="VOTRE CODE"
                    value={accessCode}
                    error={errors.accessCode}
                    hint="Fourni par votre contact DuckFlow."
                    className="font-mono uppercase"
                    onChange={(e) => {
                        setAccessCode(e.target.value.toUpperCase());
                        clearError("accessCode");
                    }}
                />

                <Button type="submit" variant="accent" size="lg" disabled={loading} aria-busy={loading} className="w-full">
                    {loading ? (
                        <>
                            <Loader2 className="h-4 w-4 motion-safe:animate-spin" />
                            Création du compte…
                        </>
                    ) : (
                        <>
                            Créer mon compte
                            <ArrowRight className="h-4 w-4" />
                        </>
                    )}
                </Button>
            </form>

            <p className="mt-6 text-center text-sm text-ink-muted">
                Déjà un compte ?{" "}
                <Link to="/auth/login" className={linkClass}>
                    Se connecter
                </Link>
            </p>
        </AuthSplit>
    );
}

export default SignUpDefault;
